
import React from 'react';
import { useLanguage } from './LanguageContext';
import { TRANSLATIONS } from '../constants';
import { Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { ViewType } from '../types';

interface FooterProps {
  onNavigate: (view: ViewType) => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  const { language } = useLanguage();
  const t = TRANSLATIONS[language];

  const sections = [
    {
      title: "Firm",
      links: [
        { name: "Our Story", view: "story" as ViewType },
        { name: "Our Team", view: "team" as ViewType },
        { name: "Careers", view: "careers" as ViewType }
      ]
    },
    {
      title: "Services",
      links: [
        { name: "Immigration", view: "immigration" as ViewType },
        { name: "Family Law", view: "family-law" as ViewType },
        { name: "SIJS", view: "sijs" as ViewType },
        { name: "Traffic Violations", view: "traffic" as ViewType },
        { name: "Car Accidents", view: "accidents" as ViewType },
        { name: "Workers Comp", view: "workers-comp" as ViewType },
        { name: "Military Law", view: "military" as ViewType }
      ]
    },
    {
      title: "Resources",
      links: [
        { name: "Legal Blog", view: "blog" as ViewType },
        { name: "Case Studies", view: "cases" as ViewType },
        { name: "FAQs", view: "faqs" as ViewType }
      ]
    },
    {
      title: "Support",
      links: [
        { name: "Privacy Policy", view: "privacy" as ViewType },
        { name: "Terms of Use", view: "terms" as ViewType },
        { name: "Cookie Settings", view: "cookies" as ViewType }
      ]
    }
  ];

  return (
    <footer className="bg-[#050505] text-white py-16 px-4 md:px-8 border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-12">
          <div className="text-left">
            <button onClick={() => onNavigate('home')} className="mb-2 block flex flex-col items-start leading-tight">
              <div className="text-[10px] font-bold tracking-[0.2em] text-[#c5a059] uppercase opacity-80 mb-0.5">
                Law Offices of
              </div>
              <div className="text-2xl font-serif font-black tracking-tight text-white flex items-baseline">
                AKIL TIRANA<span className="text-[#c5a059] font-light ml-1.5 italic">Esq.</span>
              </div>
            </button>
            <div className="text-[#c5a059] text-[10px] font-bold tracking-[0.3em] uppercase ml-1 mt-2">
              {t.footerTagline}
            </div>
          </div>

          <div className="flex gap-6">
            <a href="https://facebook.com" target="_blank" rel="noopener" className="text-gray-400 hover:text-[#c5a059] transition-colors"><Facebook className="w-5 h-5" /></a>
            <a href="https://twitter.com" target="_blank" rel="noopener" className="text-gray-400 hover:text-[#c5a059] transition-colors"><Twitter className="w-5 h-5" /></a>
            <a href="https://linkedin.com" target="_blank" rel="noopener" className="text-gray-400 hover:text-[#c5a059] transition-colors"><Linkedin className="w-5 h-5" /></a>
            <a href="https://instagram.com" target="_blank" rel="noopener" className="text-gray-400 hover:text-[#c5a059] transition-colors"><Instagram className="w-5 h-5" /></a>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16 text-sm text-gray-500">
          {sections.map(section => (
            <div key={section.title}>
              <h5 className="text-white font-bold mb-4 uppercase tracking-widest text-xs">{section.title}</h5>
              <ul className="space-y-2">
                {section.links.map(link => (
                  <li key={link.name}>
                    <button
                      onClick={() => onNavigate(link.view)}
                      className="hover:text-white transition-colors text-left"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-12 border-t border-white/5 space-y-8">
          <div className="max-w-4xl">
            <p className="text-[10px] md:text-xs text-gray-600 leading-relaxed font-light uppercase tracking-wide">
              {t.footerDisclaimer}
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center text-[11px] text-gray-500 gap-4 font-medium tracking-wider">
            <p className="uppercase">{t.footerRights}</p>
            <p className="opacity-50">DESIGNED WITH EXCELLENCE BY PREMIUM UX</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
